// TTL-aware plaintext envelope.
//
// Ported from DisappearingMessagePayload.swift. The TTL must be encrypted
// (otherwise a hostile server can grep for "all messages with TTL > 0" and
// selectively retain them), so it lives *inside* the AES-GCM ciphertext, not
// inside the outer kaether:e2ee envelope.
//
// Wire format: a JSON object prefixed with `kaether:eph:v1:` so receivers can
// distinguish a TTL-bearing payload from a plain string. The prefix also gives
// us a future migration path — bump `v1` when the schema needs to add fields.
//
//   With timer:    kaether:eph:v1:{"text":"hi","ttlSeconds":300,"sentAt":17...}
//   Without timer: hi    (raw string, exactly as before — backwards-compat)
//
// `sentAt` is millisecond epoch so all clients agree on the same wall-clock
// deadline regardless of when the message was delivered.

import { DisappearingMessageDuration } from "./DisappearingMessageDuration";

export interface DisappearingMessagePayload {
  readonly text: string;
  readonly ttlSeconds: number;
  readonly sentAt: number; // ms since epoch (matches iOS millisecondsSince1970)
}

export function payloadDuration(p: DisappearingMessagePayload): DisappearingMessageDuration {
  return DisappearingMessageDuration.fromSeconds(p.ttlSeconds);
}

export function payloadExpiresAt(p: DisappearingMessagePayload): Date | null {
  if (p.ttlSeconds <= 0) return null;
  return new Date(p.sentAt + p.ttlSeconds * 1000);
}

// ---------------------------------------------------------------------------
// Wire format
// ---------------------------------------------------------------------------

const PREFIX = "kaether:eph:v1:";

export const DisappearingMessageWireFormat = {
  prefix: PREFIX,

  /**
   * Wraps `text` in a TTL-bearing payload when `duration` is non-`.off`.
   * For `.off`, returns `text` unchanged so the wire format stays
   * byte-for-byte identical to a non-disappearing send.
   */
  wrap(
    text: string,
    duration: DisappearingMessageDuration,
    sentAt: Date = new Date(),
  ): string {
    if (duration.isOff) return text;
    const payload: DisappearingMessagePayload = {
      text,
      ttlSeconds: duration.seconds,
      sentAt: sentAt.getTime(),
    };
    let json: string;
    try {
      json = JSON.stringify(payload);
    } catch {
      // JSON.stringify can't actually fail for this shape; if it does,
      // degrade to a non-disappearing send rather than dropping the message
      // entirely. The user gets weaker semantics, not silence.
      return text;
    }
    return PREFIX + json;
  },

  /**
   * Reverse of `wrap`. Plain strings (no prefix) come back as `Unwrapped`
   * with payload=null — never throws — so legacy messages still render.
   */
  unwrap(decryptedText: string): {
    text: string;
    payload: DisappearingMessagePayload | null;
    hasTimer: boolean;
    ttlSeconds: number;
  } {
    if (!decryptedText.startsWith(PREFIX)) {
      return { text: decryptedText, payload: null, hasTimer: false, ttlSeconds: 0 };
    }
    const json = decryptedText.slice(PREFIX.length);
    let payload: DisappearingMessagePayload | null = null;
    try {
      const raw = JSON.parse(json) as Record<string, unknown>;
      if (
        typeof raw.text === "string" &&
        typeof raw.ttlSeconds === "number" &&
        typeof raw.sentAt === "number"
      ) {
        payload = {
          text: raw.text,
          ttlSeconds: raw.ttlSeconds,
          sentAt: raw.sentAt,
        };
      }
    } catch {
      // A `kaether:eph:v1:` prefix that fails to parse is surfaced as a
      // legacy text message rather than dropped. Prefer showing garbled
      // content over silently losing the message.
    }
    if (payload === null) {
      return { text: decryptedText, payload: null, hasTimer: false, ttlSeconds: 0 };
    }
    return {
      text: payload.text,
      payload,
      hasTimer: true,
      ttlSeconds: payload.ttlSeconds,
    };
  },
} as const;
